import { useState } from 'react'
import { ItemPickerDropdown } from './item-picker-dropdown'
import { useLineItemRow } from '@/hooks/use-line-item-row'
import type { PricingItem, LineItem, CreateLineItemPayload } from '@/types'

const DeleteIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>
)

const WrenchIcon = () => (
  <svg width="16" height="16" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
  </svg>
)

const fmt = (v: number) => `$${v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const selectAll = (e: React.MouseEvent<HTMLInputElement>) => (e.target as HTMLInputElement).select()

interface EquipmentSectionProps {
  lineItems: LineItem[]
  pricingItems: PricingItem[]
  section?: string
  serviceFilter?: string
  label?: string
  defaultFuelChargePercent?: number
  onAddItem: (payload: CreateLineItemPayload) => Promise<void>
  onUpdateItem: (itemId: string, data: Partial<CreateLineItemPayload> & { lineTotal?: number }) => Promise<void>
  onDeleteItem: (itemId: string) => Promise<void>
}

export function EquipmentSection({ lineItems, pricingItems, section, serviceFilter, label, defaultFuelChargePercent = 18, onAddItem, onUpdateItem, onDeleteItem }: EquipmentSectionProps) {
  const fuelChargeDecimal = defaultFuelChargePercent / 100
  const equipmentItems = pricingItems.filter((p) => {
    if (p.category !== 'equipment') return false
    if (!serviceFilter) return true
    if (!p.service) return false
    return p.service.split(',').map(s => s.trim()).includes(serviceFilter)
  })
  const rows = lineItems.filter((li) => li.category === 'equipment' && li.section === (section || null))

  const handleSelect = async (item: PricingItem) => {
    // Use per-service rate if available, otherwise fall back to base rate
    const svcRates = serviceFilter && item.serviceRates?.[serviceFilter]
    const effectiveRate = svcRates ? svcRates.rate : item.rate
    await onAddItem({
      description: item.name,
      category: 'equipment',
      quantity: 1,
      unit: 'hr',
      rate: effectiveRate,
      pricingItemId: item.id,
      hoursPerDay: 8,
      daysPerWeek: 1,
      fuelCharge: item.fuelCharge ?? fuelChargeDecimal,
      section: section,
    })
  }

  const handleFieldChange = async (li: LineItem, updates: Record<string, number>) => {
    // Compute from the row's FULL current field set (merged over the store item),
    // never from `{ ...storeItem, oneField }` — so the persisted lineTotal always
    // equals what the row displays, even when several fields changed at once.
    const m = { ...li, ...updates }
    const eqCost = (m.quantity || 0) * (m.hoursPerDay || 0) * (m.daysPerWeek || 0) * m.rate
    const lineTotal = eqCost + eqCost * (m.fuelCharge || 0)
    await onUpdateItem(li.id, {
      quantity: m.quantity,
      hoursPerDay: m.hoursPerDay,
      daysPerWeek: m.daysPerWeek,
      fuelCharge: m.fuelCharge,
      lineTotal,
    })
  }

  const equipTotal = rows.reduce((sum, li) => {
    const eqCost = (li.quantity || 0) * (li.hoursPerDay || 0) * (li.daysPerWeek || 0) * li.rate
    return sum + eqCost + eqCost * (li.fuelCharge || 0)
  }, 0)

  return (
    <div className="sc-section">
      <div className="sc-section-header">
        <WrenchIcon />
        <span className="sc-section-title">{label ?? 'Equipment'}</span>
        <div className="sc-section-actions" onClick={(e) => e.stopPropagation()}>
          <ItemPickerDropdown items={equipmentItems} onSelect={handleSelect} serviceFilter={serviceFilter} />
        </div>
      </div>

      <div className="sc-table-wrap">
        <table className="sc-table">
          <thead>
            <tr>
              <th>Equipment</th>
              <th>Qty</th>
              <th>Hrs/Day</th>
              <th>Days</th>
              <th>Rate/Hr</th>
              <th>Equip. Cost</th>
              <th>Fuel %</th>
              <th>Fuel $</th>
              <th className="col-right">Total</th>
              <th className="col-action"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((li) => (
              <EquipmentRow
                key={li.id}
                item={li}
                defaultFuelChargeDecimal={fuelChargeDecimal}
                onFieldChange={handleFieldChange}
                onDelete={() => onDeleteItem(li.id)}
                onRename={(id, description) => onUpdateItem(id, { description })}
              />
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={8} className="col-right"><strong>Total</strong></td>
                <td className="col-right"><span className="sc-foot-total">{fmt(equipTotal)}</span></td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}

function EquipmentRow({
  item, defaultFuelChargeDecimal = 0.18, onFieldChange, onDelete, onRename,
}: {
  item: LineItem
  defaultFuelChargeDecimal?: number
  onFieldChange: (li: LineItem, updates: Record<string, number>) => Promise<void>
  onDelete: () => void
  onRename: (itemId: string, description: string) => Promise<void>
}) {
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState(item.description)

  const { values, setField, onFocus, onBlur } = useLineItemRow(
    item,
    (it) => ({
      quantity: it.quantity,
      hoursPerDay: it.hoursPerDay || 8,
      daysPerWeek: it.daysPerWeek || 1,
      rate: it.rate,
      fuelCharge: it.fuelCharge || defaultFuelChargeDecimal,
    }),
    (v) => { onFieldChange(item, v) },
  )

  const fuelDisplay = (() => {
    const pct = (values.fuelCharge * 100)
    return pct % 1 === 0 ? String(pct) : String(parseFloat(pct.toFixed(4)))
  })()

  const equipmentCost = values.quantity * values.hoursPerDay * values.daysPerWeek * values.rate
  const fuelAmount = equipmentCost * values.fuelCharge
  const total = equipmentCost + fuelAmount

  return (
    <tr>
      <td className="col-name">
        {editingName ? (
          <input
            className="sc-input"
            style={{ minWidth: '120px' }}
            value={nameValue}
            autoFocus
            onChange={(e) => setNameValue(e.target.value)}
            onBlur={() => { setEditingName(false); onRename(item.id, nameValue) }}
            onKeyDown={(e) => { if (e.key === 'Enter') { setEditingName(false); onRename(item.id, nameValue) } if (e.key === 'Escape') { setEditingName(false); setNameValue(item.description) } }}
          />
        ) : (
          <span className="sc-input-rename" title="Click to rename" onClick={() => setEditingName(true)}>{nameValue}</span>
        )}
      </td>
      <td><input type="number" className="sc-input" value={values.quantity || ''}
        onChange={(e) => setField('quantity', parseFloat(e.target.value) || 0)}
        onFocus={onFocus} onBlur={onBlur} onDoubleClick={selectAll} min={0} /></td>
      <td><input type="number" className="sc-input" value={values.hoursPerDay || ''}
        onChange={(e) => setField('hoursPerDay', parseFloat(e.target.value) || 0)}
        onFocus={onFocus} onBlur={onBlur} onDoubleClick={selectAll} min={0} /></td>
      <td><input type="number" className="sc-input" value={values.daysPerWeek || ''}
        onChange={(e) => setField('daysPerWeek', parseFloat(e.target.value) || 0)}
        onFocus={onFocus} onBlur={onBlur} onDoubleClick={selectAll} min={0} /></td>
      <td>
        <span className="sc-input-readonly" title="Rate is set by admin in Pricing">{fmt(values.rate).replace('$', '')}</span>
      </td>
      <td>{fmt(equipmentCost)}</td>
      <td>
        <span className="sc-input-readonly" title="Fuel charge is set by admin in Pricing">{fuelDisplay}%</span>
      </td>
      <td>{fmt(fuelAmount)}</td>
      <td className="col-right"><strong>{fmt(total)}</strong></td>
      <td className="col-action">
        <button onClick={onDelete} className="sc-icon-btn" title="Delete"><DeleteIcon /></button>
      </td>
    </tr>
  )
}
